import { priceToNumber } from '../function/format';

export const isRequired = (fieldName = '값') => {
  return (v: string | null | undefined) => !!v || `${fieldName}을(를) 입력해주세요.`;
};

export const isLengthBetween = (min: number, max: number, fieldName = '값') => {
  return (v: string) => {
    if (!v) return `${fieldName}을(를) 입력해주세요.`;
    const len = v.length;
    return (len >= min && len <= max) || `${fieldName}은(는) ${min}~${max}자 사이여야 합니다.`;
  };
};

export const isEmail = () => {
  return (v: string) => {
    if (!v) return true;
    return /.+@.+\..+/.test(v) || '유효한 이메일 주소를 입력해주세요.';
  };
};

export const isPassword = () => {
  return (v: string | null | undefined) => {
    if (!v) return true;

    if (/\s/.test(v)) return '비밀번호에는 공백을 사용할 수 없습니다.';

    if (v.length < 8 || v.length > 20) return '비밀번호는 8~20자 이내로 입력해주세요.';

    const hasLetter = /[A-Za-z]/.test(v);
    const hasNumber = /\d/.test(v);
    const hasSpecial = /[~!@#$%^&*()+\-_=]/.test(v);

    const count = Number(hasLetter) + Number(hasNumber) + Number(hasSpecial);

    if (count < 2) return '비밀번호는 영문, 숫자, 특수문자 중 2가지 이상을 포함해야 합니다.';

    return true;
  };
};

export const isPhone = (fieldName = '전화번호') => {
  return (v: string | null | undefined) => {
    if (!v) return true;

    // 숫자만 추출
    const numbers = v.replace(/[^\d]/g, '');

    // 1️⃣ 휴대폰 (010, 011, 016~019) 10~11자리
    const mobileRegex = /^01([0|1|6-9])\d{7,8}$/;

    // 2️⃣ 집전화 (02, 031, 032, 033, 041, 042 등) 9~10자리
    const landlineRegex = /^(0(2|3[1-3]|4[1-4]|5[1-5]|6[1-4]))\d{6,8}$/;

    if (mobileRegex.test(numbers) || landlineRegex.test(numbers)) {
      return true; // ✅ 유효한 전화번호
    }

    return `${fieldName} 형식이 올바르지 않습니다.`;
  };
};

export const isMaxLength = (fieldName = '값', max: number) => {
  return (v: string | null | undefined) => {
    if (!String(v)) return true; // 비어 있으면 다른 rule (예: isRequired)에서 처리
    return String(v).length <= max || `${fieldName}은(는) 최대 ${max}자 이내로 입력해주세요.`;
  };
};
export const isMinNumber = (fieldName = '값', min: number) => {
  return (v: string | null | undefined) => {
    if (!v) return true;
    return priceToNumber(v) >= min || `${fieldName}은(는) ${min} 이상이어야 합니다.`;
  };
};

export const isMaxNumber = (fieldName = '값', max: number) => {
  return (v: string | null | undefined) => {
    if (!v) return true;
    return priceToNumber(v) < max || `${fieldName}은(는) ${max} 미만이어야 합니다.`;
  };
};
/**
 * 문자열만 허용 (영문 + 한글)
 */
export const isOnlyString = (fieldName = '값') => {
  return (v: string | null | undefined) => {
    if (!v) return true;
    const regex = /^[a-zA-Z가-힣\s]+$/;
    return regex.test(v) || `${fieldName}은(는) 문자만 입력 가능합니다.`;
  };
};

/**
 * 숫자만 허용
 */
export const isOnlyNumber = (fieldName = '값') => {
  return (v: string | null | undefined) => {
    if (!v) return true;
    const regex = /^[0-9]+$/;
    return regex.test(v) || `${fieldName}은(는) 숫자만 입력 가능합니다.`;
  };
};

export const isBusinessNumber = () => {
  return (v: string | null | undefined) => {
    if (!v) return true;
    const regex = /^[0-9]{3}-[0-9]{2}-[0-9]{5}$/;
    return regex.test(v) || `사업자등록번호 형식이 올바르지 않습니다.`;
  };
};

/**
 * 아이디: 최대 15자, 영문·숫자·기본특수문자(. , -, _)만 허용
 */
export const isId = () => {
  return (v: string | null | undefined) => {
    if (!v) return true;

    if (v.length > 15) return '아이디는 최대 15자까지 입력 가능합니다.';

    if (!/^[a-zA-Z0-9.,\-_]+$/.test(v))
      return '아이디는 영문, 숫자, 기본특수문자(. - _)만 사용할 수 있습니다.';

    return true;
  };
};
export const passwordMatch = (newPassword: string) => {
  return (v: string | null | undefined) => {
    if (!v) return true;
    return v === newPassword || '비밀번호가 일치하지 않습니다.';
  };
};

export const isOnlyEnglishAndNumber = (fieldName = '값') => {
  return (v: string | null | undefined) => {
    if (!v) return true;
    const regex = /^[a-zA-Z0-9]+$/;
    return regex.test(v) || `${fieldName}은(는) 영문과 숫자만 입력 가능합니다.`;
  };
};

export const isOnlyKoreanAndEnglish = (fieldName = '값') => {
  return (v: string | null | undefined) => {
    if (!v) return true;
    const regex = /^[가-힣a-zA-Z]+$/;
    return regex.test(v) || `${fieldName}은(는) 한글과 영문만 입력 가능합니다.`;
  };
};

export const isNotSpecialCharacters = (fieldName = '값') => {
  return (v: string | null | undefined) => {
    if (!v) return true;
    const regex = /^[ㄱ-ㅎㅏ-ㅣ가-힣a-zA-Z0-9 ]+$/;
    return regex.test(v) || `${fieldName}은(는) 특수문자를 포함할 수 없습니다.`;
  };
};

/**
 * 한글, 영문, 숫자만 허용
 */
export const isOnlyKoreanEnglishNumber = (fieldName = '값') => {
  return (v: string | null | undefined) => {
    if (!v) return true;
    const regex = /^[ㄱ-ㅎㅏ-ㅣ가-힣a-zA-Z0-9 ]+$/;
    return regex.test(v) || `${fieldName}은(는) 한글, 영문, 숫자만 가능합니다.`;
  };
};
